import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AppLayout } from './layouts/AppLayout';
import { AuthGuard } from './guards/AuthGuard';
import { useAuthStore } from './stores/authStore';

// =============================================================================
// Lazy Loading Configuration
// =============================================================================
// Components are split into chunks that load on demand, reducing initial bundle
// size from ~4.5MB to ~1.5MB for first load.
//
// Chunk groups:
// - marketing: Landing, Features, Pricing, About (~100KB)
// - auth: Login, Signup (~50KB)
// - dashboard: Main dashboard page (~200KB)
// - analytics: All analytics pages (~300KB)
// - data-explorer: Data grid pages (~400KB)
// - source-dashboards: Source-specific dashboards (~300KB)
// - ai-features: AI query, recommendations, monitoring (~250KB)
// - help: Help center pages (~150KB)
// =============================================================================

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="Loading..." />
  </div>
);

// Marketing Pages (Public) - Lazy loaded
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const FeaturesPage = lazy(() =>
  import('./pages/FeaturesPage').then((m) => ({ default: m.FeaturesPage }))
);
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() =>
  import('./pages/SignupPage').then((m) => ({ default: m.SignupPage }))
);

// Core App Pages (Protected) - Lazy loaded
const DashboardPage = lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.DashboardPage }))
);
const AnalyticsPage = lazy(() =>
  import('./pages/Analytics').then((m) => ({ default: m.AnalyticsPage }))
);
const AlertsPage = lazy(() => import('./pages/Alerts').then((m) => ({ default: m.AlertsPage })));
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.SettingsPage }))
);
const KPICatalog = lazy(() =>
  import('./pages/KPICatalog').then((m) => ({ default: m.KPICatalog }))
);
const TemplateGallery = lazy(() =>
  import('./pages/TemplateGallery').then((m) => ({ default: m.TemplateGallery }))
);

// Dashboard Management - Lazy loaded
const MyDashboards = lazy(() =>
  import('./pages/Dashboards').then((m) => ({ default: m.MyDashboards }))
);
const DashboardView = lazy(() =>
  import('./pages/Dashboards').then((m) => ({ default: m.DashboardView }))
);
const DashboardEditor = lazy(() =>
  import('./pages/Dashboards').then((m) => ({ default: m.DashboardEditor }))
);

// AI Features - Lazy loaded
const AskAI = lazy(() => import('./pages/AskAI').then((m) => ({ default: m.AskAI })));
const AIRecommendationsPage = lazy(() =>
  import('./pages/AIRecommendations').then((m) => ({ default: m.AIRecommendationsPage }))
);
// AgenticDashboard available for future use
// const AgenticDashboard = lazy(() =>
//   import('./pages/AgenticDashboard').then((m) => ({ default: m.default }))
// );

// Operations - Lazy loaded
const OrderLifecycle = lazy(() =>
  import('./pages/OrderLifecycle').then((m) => ({ default: m.OrderLifecycle }))
);
const InventoryHealth = lazy(() =>
  import('./pages/InventoryHealth').then((m) => ({ default: m.InventoryHealth }))
);

// Data Management - Lazy loaded
const DataImport = lazy(() =>
  import('./pages/DataImport').then((m) => ({ default: m.DataImport }))
);
const Connectors = lazy(() =>
  import('./pages/Connectors').then((m) => ({ default: m.Connectors }))
);
const ETLHistoryPage = lazy(() =>
  import('./pages/ETLHistory').then((m) => ({ default: m.ETLHistoryPage }))
);

// Data Explorer Pages - Lazy loaded
const DataExplorer = lazy(() => import('./pages/Data').then((m) => ({ default: m.DataExplorer })));
const OrdersData = lazy(() => import('./pages/Data').then((m) => ({ default: m.OrdersData })));
const ProductsData = lazy(() => import('./pages/Data').then((m) => ({ default: m.ProductsData })));
const CustomersData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.CustomersData }))
);
const InventoryData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.InventoryData }))
);
const ChannelsData = lazy(() => import('./pages/Data').then((m) => ({ default: m.ChannelsData })));
const InsightsData = lazy(() => import('./pages/Data').then((m) => ({ default: m.InsightsData })));
const AlertsData = lazy(() => import('./pages/Data').then((m) => ({ default: m.AlertsData })));
const ErrorLogsData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.ErrorLogsData }))
);
const OrderDetail = lazy(() => import('./pages/Data').then((m) => ({ default: m.OrderDetail })));
const ProductDetail = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.ProductDetail }))
);
const CustomerDetail = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.CustomerDetail }))
);
const InsightDetail = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.InsightDetail }))
);
const AlertDetail = lazy(() => import('./pages/Data').then((m) => ({ default: m.AlertDetail })));
const ErrorDetail = lazy(() => import('./pages/Data').then((m) => ({ default: m.ErrorDetail })));
const SalesVelocityData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.SalesVelocityData }))
);
const ProductReviewsData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.ProductReviewsData }))
);
const CustomerHealthData = lazy(() =>
  import('./pages/Data').then((m) => ({ default: m.CustomerHealthData }))
);

// Source Dashboards - Lazy loaded
const SourceDashboardsPage = lazy(() =>
  import('./pages/Source').then((m) => ({ default: m.SourceDashboardsPage }))
);
const SourceDashboardPage = lazy(() =>
  import('./pages/Source').then((m) => ({ default: m.SourceDashboardPage }))
);
const CrossSourceComparisonPage = lazy(() =>
  import('./pages/Source').then((m) => ({ default: m.CrossSourceComparisonPage }))
);
const SourceInsightsPage = lazy(() =>
  import('./pages/Source').then((m) => ({ default: m.SourceInsightsPage }))
);

// AI Monitoring - Lazy loaded
const AIMonitoringDashboard = lazy(() =>
  import('./pages/AIMonitoring').then((m) => ({ default: m.AIMonitoringDashboard }))
);
const AIRequestsData = lazy(() =>
  import('./pages/AIMonitoring').then((m) => ({ default: m.AIRequestsData }))
);
const AIChatsData = lazy(() =>
  import('./pages/AIMonitoring').then((m) => ({ default: m.AIChatsData }))
);
const AITokenUsageData = lazy(() =>
  import('./pages/AIMonitoring').then((m) => ({ default: m.AITokenUsageData }))
);
const AIErrorsData = lazy(() =>
  import('./pages/AIMonitoring').then((m) => ({ default: m.AIErrorsData }))
);

// Help Center - Lazy loaded
const HelpLayout = lazy(() => import('./pages/Help').then((m) => ({ default: m.HelpLayout })));
const HelpCenter = lazy(() => import('./pages/Help').then((m) => ({ default: m.HelpCenter })));
const GettingStarted = lazy(() =>
  import('./pages/Help').then((m) => ({ default: m.GettingStarted }))
);
const ScreenGuide = lazy(() => import('./pages/Help').then((m) => ({ default: m.ScreenGuide })));
const KPIReference = lazy(() => import('./pages/Help').then((m) => ({ default: m.KPIReference })));
const FeatureCatalog = lazy(() =>
  import('./pages/Help').then((m) => ({ default: m.FeatureCatalog }))
);
const Glossary = lazy(() => import('./pages/Help').then((m) => ({ default: m.Glossary })));
const FAQ = lazy(() => import('./pages/Help').then((m) => ({ default: m.FAQ })));
const DemoGuide = lazy(() => import('./pages/Help').then((m) => ({ default: m.DemoGuide })));

/**
 * Main application component with lazy-loaded routes
 *
 * @performance Routes are code-split and loaded on demand:
 * - Initial bundle reduced from ~4.5MB to ~1.5MB
 * - Each route chunk loads only when navigated to
 * - Suspense provides loading feedback during chunk fetch
 *
 * Route Structure:
 * - Public Marketing Routes (/)
 * - Protected App Routes (/app/*)
 * - Help Center Routes (/help/*)
 */
function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
    const interval = setInterval(() => {
      checkAuth();
    }, 60000);
    return () => clearInterval(interval);
  }, [checkAuth]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ============================================ */}
        {/* Public Marketing Routes - No Auth Required  */}
        {/* ============================================ */}

        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* ============================================ */}
        {/* Protected App Routes - Auth Required        */}
        {/* ============================================ */}

        <Route
          path="/app"
          element={
            <AuthGuard>
              <AppLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/app/dashboard" replace />} />

          {/* Core Pages */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="analytics/*" element={<AnalyticsPage />} />

          {/* Operations */}
          <Route path="operations/orders" element={<OrderLifecycle />} />
          <Route path="operations/inventory" element={<InventoryHealth />} />

          {/* AI Features */}
          <Route path="ask-ai" element={<AskAI />} />
          <Route path="recommendations" element={<AIRecommendationsPage />} />

          {/* AI Monitoring */}
          <Route path="ai-monitoring" element={<AIMonitoringDashboard />} />
          <Route path="ai-monitoring/requests" element={<AIRequestsData />} />
          <Route path="ai-monitoring/chats" element={<AIChatsData />} />
          <Route path="ai-monitoring/usage" element={<AITokenUsageData />} />
          <Route path="ai-monitoring/errors" element={<AIErrorsData />} />

          {/* Resources */}
          <Route path="templates" element={<TemplateGallery />} />
          <Route path="dashboards" element={<MyDashboards />} />
          <Route path="dashboards/:id" element={<DashboardView />} />
          <Route path="dashboards/:id/edit" element={<DashboardEditor />} />
          <Route path="kpis" element={<KPICatalog />} />
          <Route path="alerts" element={<AlertsPage />} />

          {/* Data Explorer */}
          <Route path="data" element={<DataExplorer />} />
          <Route path="data/overview" element={<DataExplorer />} />
          <Route path="data/orders" element={<OrdersData />} />
          <Route path="data/orders/:orderId" element={<OrderDetail />} />
          <Route path="data/products" element={<ProductsData />} />
          <Route path="data/products/:productId" element={<ProductDetail />} />
          <Route path="data/customers" element={<CustomersData />} />
          <Route path="data/customers/:customerId" element={<CustomerDetail />} />
          <Route path="data/inventory" element={<InventoryData />} />
          <Route path="data/channels" element={<ChannelsData />} />
          <Route path="data/insights" element={<InsightsData />} />
          <Route path="data/insights/:insightId" element={<InsightDetail />} />
          <Route path="data/alerts" element={<AlertsData />} />
          <Route path="data/alerts/:alertId" element={<AlertDetail />} />
          <Route path="data/errors" element={<ErrorLogsData />} />
          <Route path="data/errors/:errorId" element={<ErrorDetail />} />
          <Route path="data/sales-velocity" element={<SalesVelocityData />} />
          <Route path="data/product-reviews" element={<ProductReviewsData />} />
          <Route path="data/customer-health" element={<CustomerHealthData />} />

          {/* Source Dashboards */}
          <Route path="sources" element={<SourceDashboardsPage />} />
          <Route path="sources/comparison" element={<CrossSourceComparisonPage />} />
          <Route path="sources/insights" element={<SourceInsightsPage />} />
          <Route path="sources/insights/:insightId" element={<SourceInsightsPage />} />
          <Route path="sources/:source" element={<SourceDashboardPage />} />

          {/* AI Demo Insights redirect */}
          <Route
            path="ai-demo-insights"
            element={<Navigate to="/app/sources/insights" replace />}
          />

          {/* Data Management */}
          <Route path="data-import" element={<DataImport />} />
          <Route path="connectors" element={<Connectors />} />
          <Route path="etl-history" element={<ETLHistoryPage />} />

          {/* Settings */}
          <Route path="settings/*" element={<SettingsPage />} />
        </Route>

        {/* Help Center Routes - Protected */}
        <Route
          path="/help"
          element={
            <AuthGuard>
              <Suspense fallback={<PageLoader />}>
                <HelpLayout />
              </Suspense>
            </AuthGuard>
          }
        >
          <Route index element={<HelpCenter />} />
          <Route path="overview" element={<HelpCenter />} />
          <Route path="getting-started" element={<GettingStarted />} />
          <Route path="screens" element={<Navigate to="/help" replace />} />
          <Route path="screens/:screenId" element={<ScreenGuide />} />
          <Route path="features" element={<FeatureCatalog />} />
          <Route path="kpis" element={<KPIReference />} />
          <Route path="glossary" element={<Glossary />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="demo" element={<DemoGuide />} />
        </Route>

        {/* ============================================ */}
        {/* Legacy Route Redirects                      */}
        {/* ============================================ */}

        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/analytics/*" element={<Navigate to="/app/analytics" replace />} />
        <Route path="/operations/*" element={<Navigate to="/app/operations/orders" replace />} />
        <Route path="/recommendations" element={<Navigate to="/app/recommendations" replace />} />
        <Route path="/templates" element={<Navigate to="/app/templates" replace />} />
        <Route path="/kpis" element={<Navigate to="/app/kpis" replace />} />
        <Route path="/alerts" element={<Navigate to="/app/alerts" replace />} />
        <Route path="/data-import" element={<Navigate to="/app/data-import" replace />} />
        <Route path="/connectors" element={<Navigate to="/app/connectors" replace />} />
        <Route path="/settings/*" element={<Navigate to="/app/settings" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
